
import { useState, useEffect } from 'react';
import { useAuth } from './useAuth';
import { supabase } from '@/lib/supabase';
import { Family, Parent, Student } from '@/types/models';
import { toast } from 'sonner';
import { FamilyService } from '@/services/familyService';

export const useFamilyDetails = (familyId: string) => {
  const [family, setFamily] = useState<Family | null>(null);
  const [parents, setParents] = useState<Parent[]>([]);
  const [students, setStudents] = useState<Student[]>([]);
  const [loading, setLoading] = useState(true);
  const [isAddParentDialogOpen, setIsAddParentDialogOpen] = useState(false);
  const [isAddStudentDialogOpen, setIsAddStudentDialogOpen] = useState(false);
  const [isAddingParent, setIsAddingParent] = useState(false);
  const [isAddingStudent, setIsAddingStudent] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    if (!familyId) return;
    
    const loadFamilyData = async () => {
      setLoading(true);
      try {
        // Fetch family
        const { data: familyData, error: familyError } = await supabase
          .from('families')
          .select('*')
          .eq('id', familyId)
          .single();
        
        if (familyError) throw familyError;
        setFamily(familyData as Family);
        
        // Fetch parents
        const { data: parentsData, error: parentsError } = await supabase
          .from('parents')
          .select('*')
          .eq('family_id', familyId);
          
        if (parentsError) throw parentsError;
        setParents(parentsData as Parent[]);
        
        // Fetch students
        const { data: studentsData, error: studentsError } = await supabase
          .from('students')
          .select('*')
          .eq('family_id', familyId);
          
        if (studentsError) throw studentsError;
        setStudents(studentsData as Student[]);
      } catch (error) {
        console.error('Error loading family details:', error);
        toast.error('Failed to load family details');
      } finally {
        setLoading(false);
      }
    };
    
    loadFamilyData();
  }, [familyId]);
  
  const handleAddParent = async (parentData: any) => {
    console.log('🚀 handleAddParent called with:', parentData);
    console.log('🚀 is_primary value:', parentData.is_primary);
    
    setIsAddingParent(true);
    try {
      console.log('🚀 Calling FamilyService.addParent...');
      // Use FamilyService to create parent (which now handles primary parent logic)
      const newParent = await FamilyService.addParent(parentData);
      console.log('🚀 FamilyService.addParent returned:', newParent);
      
      if (newParent) {
        // If primary status was set, refetch all parents to ensure consistency
        if (parentData.is_primary === true) {
          console.log('🚀 Primary parent detected, refetching all parents...');
          
          // Add a small delay to ensure database transaction is committed
          await new Promise(resolve => setTimeout(resolve, 100));
          
          // Refetch all parents for this family to get the correct primary status
          const refreshedParents = await FamilyService.getParentsByFamily(parentData.family_id);
          console.log('🚀 Refreshed parents:', refreshedParents.map(p => ({ name: `${p.first_name} ${p.last_name}`, is_primary: p.is_primary })));
          
          setParents(refreshedParents);
          console.log('🚀 State updated with refreshed parents');
        } else {
          console.log('🚀 Non-primary parent, just adding to list');
          // For non-primary parents, just add to the list
          setParents(prevParents => [...prevParents, newParent]);
        }
        
        return newParent;
      } else {
        throw new Error('Failed to create parent');
      }
    } catch (error) {
      console.error('❌ Error adding parent:', error);
      toast.error('Failed to add parent');
      throw error;
    } finally {
      setIsAddingParent(false);
    }
  };
  
  const handleAddStudent = async (studentData: any) => {
    setIsAddingStudent(true);
    try {
      const { data, error } = await supabase
        .from('students')
        .insert([studentData])
        .select();
        
      if (error) throw error;
      
      if (data?.[0]) {
        setStudents(prevStudents => [...prevStudents, data[0] as Student]);
        return data[0];
      }
    } catch (error) {
      console.error('Error adding student:', error);
      toast.error('Failed to add student');
      throw error;
    } finally {
      setIsAddingStudent(false);
    }
  };
  
  const handleDeleteFamily = async (): Promise<boolean> => {
    if (!family) return false;
    
    if (!confirm(`Are you sure you want to delete ${family.name}? This will remove all associated parents and students.`)) {
      return false;
    }
    
    console.log('Starting family deletion for ID:', family.id);
    setIsDeleting(true);
    try {
      console.log('Making delete request to Supabase...');
      const { error, data } = await supabase
        .from('families')
        .delete()
        .eq('id', family.id)
        .select();
        
      console.log('Delete response:', { error, data });
        
      if (error) {
        console.error('Error deleting family:', error);
        if (error.code === 'PGRST116') {
          toast.error('Permission denied', {
            description: 'You do not have permission to delete this family.'
          });
        } else {
          toast.error('Failed to delete family', {
            description: error.message
          });
        }
        throw error;
      }
      
      console.log('Family deletion successful');
      toast.success('Family deleted successfully');
      return true;
    } catch (error) {
      console.error('Error deleting family:', error);
      return false;
    } finally {
      console.log('Setting isDeleting to false');
      setIsDeleting(false);
    }
  };

  return {
    family,
    setFamily,
    parents,
    setParents,
    students,
    setStudents,
    loading,
    isAddParentDialogOpen,
    setIsAddParentDialogOpen,
    isAddStudentDialogOpen,
    setIsAddStudentDialogOpen,
    isAddingParent,
    isAddingStudent,
    isDeleting,
    handleAddParent,
    handleAddStudent,
    handleDeleteFamily
  };
};
